import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import VehiclePlateComponent from "@/components/ui/VehiclePlate";
import VehiclePlateInput from "@/components/ui/VehiclePlateInput";
import { COUNTRIES } from "@/constants/countries";
import { useAuth } from "@/context/AuthContext";
import { UserDocument } from "@/types/user";
import { VehiclePlate } from "@/types/vehicle-plate";
import {
  formatVehiclePlateToObj,
  formatVehiclePlateToStr,
} from "@/utils/formatVehiclePlate";
import { Ionicons } from "@expo/vector-icons";
import firestore from "@react-native-firebase/firestore";
import { useRouter } from "expo-router";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  BackHandler,
  StyleSheet,
  TouchableOpacity,
} from "react-native";

type FormData = {
  followedPlates: VehiclePlate[];
};

const SettingsPlatesPage = () => {
  const { user } = useAuth();
  const router = useRouter();
  const [userData, setUserData] = useState<UserDocument | null>(null);
  const [preferredCountry, setPreferredCountry] = useState<string | undefined>(
    undefined,
  );
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormData>({
    defaultValues: {
      followedPlates: [],
    },
  });

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      try {
        const userDocRef = firestore().collection("users").doc(user.uid);
        const userDocSnap = await userDocRef.get();
        if (userDocSnap.exists()) {
          const data = userDocSnap.data() as UserDocument;
          setUserData(data);
          const initialPlates = data.followedPlates
            ? data.followedPlates.map(formatVehiclePlateToObj)
            : [];
          reset({ followedPlates: initialPlates });
          setPreferredCountry(
            (prev) =>
              prev ?? initialPlates[0]?.country ?? data.country ?? undefined,
          );
        }
      } catch (error) {
        console.error("Error loading plate settings:", error);
        Alert.alert(t("ERROR"), t("ERROR_LOADING_SETTINGS"));
      }
    };

    fetchUserData();
  }, [user, reset, t]);

  const handleSave = async (data: FormData) => {
    if (!user) return;

    const plates = Array.isArray(data.followedPlates)
      ? data.followedPlates
      : [];
    const validPlates = plates.filter(
      (p) => p && p.plate && p.plate.trim() !== "",
    );
    const formattedFollowedPlates = validPlates.map(formatVehiclePlateToStr);

    setIsSaving(true);
    try {
      const userDocRef = firestore().collection("users").doc(user.uid);
      await userDocRef.set(
        { followedPlates: formattedFollowedPlates },
        { merge: true },
      );

      reset({ followedPlates: validPlates });
    } catch (error) {
      console.error("Error saving plate settings:", error);
      Alert.alert(t("ERROR"), t("ERROR_SAVING_SETTINGS"));
    } finally {
      setIsSaving(false);
    }
  };

  const navigateBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(tabs)/settings");
    }
  }, [router]);

  const confirmDiscard = useCallback(() => {
    Alert.alert(t("UNSAVED_CHANGES_TITLE"), t("UNSAVED_CHANGES_MESSAGE"), [
      { text: t("CANCEL"), style: "cancel" },
      {
        text: t("DISCARD"),
        style: "destructive",
        onPress: () => {
          reset();
          navigateBack();
        },
      },
    ]);
  }, [navigateBack, reset, t]);

  const handleBackPress = useCallback(() => {
    if (!isDirty) {
      navigateBack();
      return;
    }
    confirmDiscard();
  }, [isDirty, navigateBack, confirmDiscard]);

  useEffect(() => {
    const onHardwareBack = () => {
      if (!isDirty) return false;
      confirmDiscard();
      return true;
    };
    const subscription = BackHandler.addEventListener(
      "hardwareBackPress",
      onHardwareBack,
    );
    return () => subscription.remove();
  }, [isDirty, confirmDiscard]);

  return (
    <>
      <CustomHeader
        title={t("PLATES")}
        type="subtitle"
        variant="tab"
        showBackButton
        onBackPress={handleBackPress}
      />
      <ThemedView style={styles.container}>
        <ThemedText style={styles.sectionTitle}>
          {t("FOLLOWED_PLATES")}
        </ThemedText>
        <Controller
          control={control}
          name="followedPlates"
          render={({ field: { onChange, value } }) => {
            const currentPlate = value && value.length > 0 ? value[0] : null;
            const isSavedPlate = !!currentPlate && !isDirty;
            const handleLocalRemove = () => onChange([]);
            const handleUnfollow = () => {
              Alert.alert(
                t("UNFOLLOW_PLATE_TITLE"),
                t("ARE_YOU_SURE_YOU_WANT_TO_UNFOLLOW_PLATE"),
                [
                  { text: t("CANCEL"), style: "cancel" },
                  {
                    text: t("UNFOLLOW"),
                    style: "destructive",
                    onPress: () => {
                      onChange([]);
                      handleSubmit(handleSave)();
                    },
                  },
                ],
              );
            };
            return (
              <ThemedView style={styles.inputRow}>
                {currentPlate ? (
                  <VehiclePlateComponent
                    country={currentPlate.country}
                    plate={currentPlate.plate}
                    onDelete={isSavedPlate ? undefined : handleLocalRemove}
                  />
                ) : (
                  <ThemedView style={styles.inputSlot}>
                    <VehiclePlateInput
                      plates={value}
                      onChange={onChange}
                      limit={1}
                      countries={COUNTRIES}
                      defaultCountry={
                        preferredCountry ?? userData?.country ?? undefined
                      }
                      renderList={false}
                      onCountryChange={setPreferredCountry}
                    />
                  </ThemedView>
                )}
                {isSavedPlate ? (
                  <TouchableOpacity
                    onPress={handleUnfollow}
                    style={styles.unfollowButton}
                    accessibilityLabel={t("DISCARD")}
                  >
                    <Ionicons name="close" size={16} color="white" />
                  </TouchableOpacity>
                ) : isSaving ? (
                  <ThemedView style={styles.spinnerSlot}>
                    <ActivityIndicator color="#0a7ea4" />
                  </ThemedView>
                ) : (
                  <Button
                    text={t("SAVE")}
                    onPress={handleSubmit(handleSave)}
                    disabled={!isDirty}
                    style={styles.saveButton}
                  />
                )}
              </ThemedView>
            );
          }}
        />
      </ThemedView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: 10,
    marginBottom: 12,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  inputSlot: {
    flex: 1,
  },
  saveButton: {
    flexShrink: 0,
    width: 100,
  },
  spinnerSlot: {
    flexShrink: 0,
    width: 100,
    height: 44,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  unfollowButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: "red",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    flexShrink: 0,
  },
});

export default SettingsPlatesPage;
